import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Products.css';
import './ProductsActions.css';
import './MobileResponsive.css';
import '../FullViewLayout.css';
import apiService from '../../services/api';
import { exportToCSV, exportToExcel, exportToPDF, printPage } from './exportUtils';

const Customers = ({ isActive, filterStatus, searchTerm, onSearchChange, onShowFilters }) => {
    const navigate = useNavigate();
    const [customers, setCustomers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(25);
    const [totalEntries, setTotalEntries] = useState(0);
    const [lastPage, setLastPage] = useState(1);

    // Filter States removed (lifted to parent)

    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [openActionId, setOpenActionId] = useState(null);
    const [showExportDropdown, setShowExportDropdown] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);

    const handleExport = (format) => {
        if (!customers || customers.length === 0) {
            alert('No customers to export');
            setShowExportDropdown(false);
            return;
        }

        const headers = ['Contact ID', 'Business Name', 'Name', 'Email', 'Mobile', 'Tax Number', 'Credit Limit', 'Balance'];
        const rows = customers.map(c => [
            c.contact_id || '', c.business_name || '', c.name || '', c.email || '', c.mobile || '',
            c.tax_number || '', c.credit_limit || '0', c.balance || '0'
        ]);

        const title = 'Customers List';
        const filename = 'customers';

        if (format === 'CSV') exportToCSV(headers, rows, filename);
        else if (format === 'Excel') exportToExcel(headers, rows, filename);
        else if (format === 'PDF') exportToPDF(title, headers, rows, filename);
        else if (format === 'Print') printPage();

        setShowExportDropdown(false);
    };

    const handlePrint = () => { printPage(); setShowExportDropdown(false); };
    const handleColumnVisibility = () => { alert('Column visibility settings'); setShowExportDropdown(false); };
    const handleExportPDF = () => { handleExport('PDF'); };

    useEffect(() => {
        if (isActive) {
            fetchCustomers();
        }
    }, [isActive, currentPage, perPage, searchTerm, filterStatus]);

    const fetchCustomers = async () => {
        setIsLoading(true);
        try {
            const data = await apiService.getContacts({
                type: 'customer',
                page: currentPage,
                per_page: perPage,
                name: searchTerm,
                status: filterStatus !== 'all' ? filterStatus : undefined
            });
            // ... rest of fetchCustomers ... (omitted for brevity in replace, but logic remains same)
            const customerList = Array.isArray(data) ? data : (data.data || []);
            setCustomers(customerList);

            if (data.total) setTotalEntries(data.total);
            if (data.last_page) setLastPage(data.last_page);
            else if (data.meta && data.meta.last_page) setLastPage(data.meta.last_page);

        } catch (err) {
            console.error('Failed to load customers', err);
            setError('Failed to load customers');
        } finally {
            setIsLoading(false);
        }
    };
    // ... toggleActionMenu, useEffect (click outside), handleSort, getSortedCustomers ...
    const toggleActionMenu = (id) => {
        setOpenActionId(openActionId === id ? null : id);
    };

    const handleViewCustomer = (customer) => {
        if (!customer) return;
        setSelectedCustomer(customer);
        setShowViewModal(true);
        setOpenActionId(null);
    };

    const handleCloseModal = () => {
        setShowViewModal(false);
        setSelectedCustomer(null);
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.action-dropdown-container')) {
                setOpenActionId(null);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortedCustomers = (list) => {
        if (!sortConfig.key) return list;
        return [...list].sort((a, b) => {
            let aVal = a[sortConfig.key] || '';
            let bVal = b[sortConfig.key] || '';
            if (typeof aVal === 'string') aVal = aVal.toLowerCase();
            if (typeof bVal === 'string') bVal = bVal.toLowerCase();

            if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    };

    return (
        <div className="full-view-inner">
            {/* Table Controls (Search, Entries) */}
            <div className="full-view-table-controls">
                <div className="table-controls-left">
                    <div className="entries-dropdown">
                        <span>Show</span>
                        <select value={perPage} onChange={(e) => {
                            setPerPage(Number(e.target.value));
                            setCurrentPage(1);
                        }}>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                        <span>entries</span>
                    </div>
                </div>

                <div className="table-controls-right">
                    <div className="tabs-search-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                            type="text"
                            placeholder="Search Customers..."
                            value={searchTerm}
                            onChange={(e) => onSearchChange(e.target.value)}
                            className="tabs-search-input"
                        />
                        <button
                            className="filter-toggle-btn"
                            onClick={onShowFilters}
                            title="Open Filters"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                            </svg>
                            Filters
                        </button>
                    </div>
                    <div className="export-dropdown-container" style={{ position: 'relative' }}>
                        <button className="export-btn" onClick={() => setShowExportDropdown(!showExportDropdown)}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                <polyline points="7 10 12 15 17 10"></polyline>
                                <line x1="12" y1="15" x2="12" y2="3"></line>
                            </svg>
                            Export ▾
                        </button>
                        {showExportDropdown && (
                            <div className="export-dropdown-menu">
                                <button onClick={() => handleExport('CSV')}><i className="fa fa-file-csv"></i> Export CSV</button>
                                <button onClick={() => handleExport('Excel')}><i className="fa fa-file-excel"></i> Export Excel</button>
                                <button onClick={() => handleExport('Print')}><i className="fa fa-print"></i> Print</button>
                                <button onClick={() => handleExport('PDF')}><i className="fa fa-file-pdf"></i> Export PDF</button>
                            </div>
                        )}
                    </div>
                    <button className="control-btn-primary" onClick={() => navigate('/contacts/customers/add')}>
                        <span className="plus-icon">+</span> Add
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="table-content-card">
                <div className="full-view-content">
                    {isLoading ? (
                        <div className="full-view-loading">
                            <h3>⏳ Loading Customers...</h3>
                            <p>Fetching customers data from server...</p>
                        </div>
                    ) : customers.length === 0 ? (
                        <div className="full-view-empty">
                            <h3>📊 No customers found</h3>
                            <p>Try adjusting filters or add a new customer.</p>
                        </div>
                    ) : (
                        <table className="full-view-table products-table">
                            <thead>
                                <tr>
                                    <th>Action</th>
                                    {[
                                        { key: 'contact_id', label: 'Contact ID' },
                                        { key: 'business_name', label: 'Business Name' },
                                        { key: 'name', label: 'Name' },
                                        { key: 'email', label: 'Email' },
                                        { key: 'mobile', label: 'Mobile' },
                                        { key: 'tax_number', label: 'Tax Number' },
                                        { key: 'credit_limit', label: 'Credit Limit' },
                                        { key: 'balance', label: 'Balance' }
                                    ].map(({ key, label }) => (
                                        <th key={key} className="sortable" onClick={() => handleSort(key)}>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                                                {label}
                                                {sortConfig.key === key ? (
                                                    sortConfig.direction === 'asc' ? '▲' : '▼'
                                                ) : (
                                                    <span style={{ color: '#ccc' }}>▼</span>
                                                )}
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {customers.length === 0 ? (
                                    <tr><td colSpan="9" style={{ textAlign: 'center', padding: '2rem' }}>No customers found.</td></tr>
                                ) : (
                                    getSortedCustomers(customers).map(customer => (
                                        <tr key={customer.id}>
                                            <td className="actions-cell">
                                                <div className="action-dropdown-container">
                                                    <button className="action-btn-trigger" onClick={(e) => {
                                                        e.stopPropagation();
                                                        toggleActionMenu(customer.id);
                                                    }}>
                                                        Actions <span className="arrow-down">▼</span>
                                                    </button>
                                                    {openActionId === customer.id && (
                                                        <div className="action-dropdown-menu">
                                                            <button onClick={() => handleViewCustomer(customer)}>View</button>
                                                            <button onClick={() => console.log('Edit', customer.id)}>Edit</button>
                                                            <button onClick={() => console.log('Delete', customer.id)}>Delete</button>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td>{customer.contact_id}</td>
                                            <td>{customer.business_name || '--'}</td>
                                            <td>{customer.name}</td>
                                            <td>{customer.email || '--'}</td>
                                            <td>{customer.mobile}</td>
                                            <td>{customer.tax_number || '--'}</td>
                                            <td>{customer.credit_limit ? `$${Number(customer.credit_limit).toFixed(2)}` : '--'}</td>
                                            <td>{customer.balance ? `$${Number(customer.balance).toFixed(2)}` : '$0.00'}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Pagination */}
            {customers.length > 0 && (
                <div className="full-view-pagination">
                    <div className="pagination-info">
                        Showing {((currentPage - 1) * perPage) + 1} to {Math.min(currentPage * perPage, totalEntries)} of {totalEntries} entries
                    </div>

                    <div className="pagination-controls">
                        <button
                            className="pagination-btn prev"
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                        >
                            Previous
                        </button>

                        <span className="pagination-page-info">
                            Page {currentPage} of {lastPage || 1}
                        </span>

                        <button
                            className="pagination-btn next"
                            onClick={() => setCurrentPage(p => Math.min(lastPage, p + 1))}
                            disabled={currentPage === lastPage}
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}

            {/* View Customer Modal */}
            {
                showViewModal && (
                    <div className="modal-overlay">
                        <div className="modal-container" style={{ maxWidth: '520px', width: '95%' }}>
                            <div className="modal-header">
                                <h2>View Customer</h2>
                                <button className="modal-close-btn" onClick={handleCloseModal}>✕</button>
                            </div>
                            <div className="modal-content" style={{ padding: '1.5rem' }}>
                                <div className="record-details">
                                    <div><strong>Name:</strong> {selectedCustomer?.name || 'N/A'}</div>
                                    <div><strong>Business Name:</strong> {selectedCustomer?.business_name || 'N/A'}</div>
                                    <div><strong>Email:</strong> {selectedCustomer?.email || 'N/A'}</div>
                                    <div><strong>Contact:</strong> {selectedCustomer?.mobile || selectedCustomer?.contact_number || 'N/A'}</div>
                                    <div><strong>Address:</strong> {selectedCustomer?.address_line_1 || selectedCustomer?.address || 'N/A'}</div>
                                    <div><strong>City:</strong> {selectedCustomer?.city || 'N/A'}</div>
                                    <div><strong>State:</strong> {selectedCustomer?.state || 'N/A'}</div>
                                    <div><strong>Country:</strong> {selectedCustomer?.country || 'N/A'}</div>
                                    <div><strong>Postal Code:</strong> {selectedCustomer?.postal_code || 'N/A'}</div>
                                    <div><strong>Tax Number:</strong> {selectedCustomer?.tax_number || 'N/A'}</div>
                                    <div><strong>Credit Limit:</strong> {selectedCustomer?.credit_limit ? `$${Number(selectedCustomer.credit_limit).toFixed(2)}` : 'N/A'}</div>
                                    <div><strong>Balance:</strong> {selectedCustomer?.balance ? `$${Number(selectedCustomer.balance).toFixed(2)}` : '$0.00'}</div>
                                    <div><strong>Status:</strong> {selectedCustomer?.status || 'Active'}</div>
                                </div>
                            </div>
                            <div className="modal-footer" style={{ justifyContent: 'flex-end' }}>
                                <button className="modal-btn close-btn" onClick={handleCloseModal}>Close</button>
                            </div>
                        </div>
                    </div>
                )
            }
        </div>
    );
};

export default Customers;
